import { z } from 'zod'

/**
 * Dinheiro no Liberty Finance: string decimal na fronteira, centavos inteiros na
 * aritmética, zero biblioteca.
 *
 * A conta que dispensa decimal.js/big.js/BigInt:
 *   NUMERIC(15,2) do backend → máx. 9.999.999.999.999,99
 *                            → em centavos: 999_999_999_999_999 ≈ 1,0 × 10^15
 *   Number.MAX_SAFE_INTEGER  = 9_007_199_254_740_991        ≈ 9,0 × 10^15
 * Todo o domínio cabe em inteiro seguro com ~9× de folga. Somar centavos é
 * EXATO e custa 0 kB (decimal.js custaria ~12 kB gzip do budget de 300).
 *
 * O ESLint bloqueia decimal.js/big.js via no-restricted-imports.
 */

/**
 * Decimal em string, como o backend serializa: "100.50", "-42.00", "0.00".
 *
 * A marca é sobre `${number}`, não sobre `string`, de propósito:
 * Intl.NumberFormat.format() aceita `StringNumericLiteral = \`${number}\`` — é
 * o que permite formatar a decimal EXATA sem passar por double. Marcar sobre
 * `string` quebraria essa atribuição e forçaria um cast em toda formatação.
 */
declare const moneyBrand: unique symbol
export type Money = `${number}` & { readonly [moneyBrand]: 'Money' }

/** Centavos inteiros. Sempre seguro dentro de NUMERIC(15,2). */
declare const centsBrand: unique symbol
export type Cents = number & { readonly [centsBrand]: 'Cents' }

/** Zero em centavos — evita `0 as Cents` espalhado pelo código. */
export const ZERO_CENTS = 0 as Cents

/** Até 13 dígitos inteiros + até 2 decimais — o domínio de NUMERIC(15,2). */
const MONEY_RE = /^-?\d{1,13}(\.\d{1,2})?$/

export const moneySchema = z
  .string()
  .regex(MONEY_RE, 'money.invalid')
  .transform((v) => v as Money)

export function parseMoney(raw: string): Money {
  return moneySchema.parse(raw)
}

export function isMoney(raw: string): raw is Money {
  return MONEY_RE.test(raw)
}

/**
 * "100.50" → 10050.
 *
 * CONCATENA STRINGS — nunca `Math.round(parseFloat(v) * 100)`. O motivo é
 * concreto: parseFloat("1.005") * 100 === 100.49999999999999, e o round devolve
 * 100 em vez de 101. Um centavo somem, o rateio não bate com o amount, e o
 * backend rejeita com 422 um formulário visivelmente correto.
 */
export function toCents(m: Money): Cents {
  const negative = m.startsWith('-')
  const body = negative ? m.slice(1) : m
  const [int = '0', frac = ''] = body.split('.')
  const value = Number(`${int}${(frac + '00').slice(0, 2)}`)
  if (!Number.isSafeInteger(value)) {
    throw new RangeError(`Valor fora do inteiro seguro: ${m}`)
  }
  return (negative ? -value : value) as Cents
}

/** 10050 → "100.50". Sempre 2 casas. */
export function fromCents(c: Cents): Money {
  const negative = c < 0
  const digits = String(Math.abs(c)).padStart(3, '0')
  return `${negative ? '-' : ''}${digits.slice(0, -2)}.${digits.slice(-2)}` as Money
}

function guard(n: number): Cents {
  // Não é paranoia: 100 itens de rateio no teto do domínio chegariam a ~10^17.
  // Dado VÁLIDO nunca chega lá (o backend exige soma == amount ≤ máximo), mas
  // dado corrompido deve estourar alto em vez de virar um total errado silencioso.
  if (!Number.isSafeInteger(n)) throw new RangeError(`Overflow de centavos: ${String(n)}`)
  return n as Cents
}

export function addCents(a: Cents, b: Cents): Cents {
  return guard(a + b)
}

export function subCents(a: Cents, b: Cents): Cents {
  return guard(a - b)
}

export function sumCents(list: readonly Cents[]): Cents {
  return list.reduce<Cents>((acc, c) => addCents(acc, c), 0 as Cents)
}

/** "10.5" e "10.50" são o MESMO dinheiro — comparar as strings diria que não. */
export function moneyEq(a: Money, b: Money): boolean {
  return toCents(a) === toCents(b)
}

/**
 * A soma do rateio bate com o total?
 * Espelha a regra do backend (422 schedule_categories_sum_mismatch). É UX —
 * feedback antes do round-trip; o servidor continua sendo a autoridade.
 */
export function sumMatches(total: Money, parts: readonly Money[]): boolean {
  return toCents(total) === sumCents(parts.map(toCents))
}

// ------------------------------------------------------------------ exibição

const formatters = new Map<string, Intl.NumberFormat>()

/**
 * Formata a string decimal EXATA — Intl.NumberFormat aceita StringNumericLiteral
 * e não converte para double no caminho.
 *
 * NUNCA fazer `fmt.format(toCents(m) / 100)`: reintroduz exatamente o float que
 * todo este módulo existe para evitar.
 */
export function formatMoney(m: Money, locale: string, currency = 'BRL'): string {
  const key = `${locale}|${currency}`
  let fmt = formatters.get(key)
  if (!fmt) {
    fmt = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    formatters.set(key, fmt)
  }
  return fmt.format(m)
}

/** Entrada do usuário ("R$ 1.234,56" em pt-BR, "1,234.56" em en) → Money. */
export function parseUserInput(input: string, locale: string): Money | null {
  const decimalSep = locale.startsWith('pt') ? ',' : '.'
  const cleaned = input
    .replace(/[^\d,.-]/g, '')
    .replace(decimalSep === ',' ? /\./g : /,/g, '') // separador de milhar fora
    .replace(decimalSep, '.')
  return isMoney(cleaned) ? cleaned : null
}
